import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import { isNumber } from '@maplecroft-typescript/utils';

import type { DatawrapperItemValue } from '../../../shared-models';

@Component({
  selector: 'grid-ui-body-item-datawrapper',
  templateUrl: './body-item-datawrapper.component.html',
  styleUrls: ['./body-item-datawrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    class: 'Datawrapper',
    '(window:message)': 'onMessage($event)',
  },
  standalone: false,
})
export class BodyItemDatawrapperComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly iframe = viewChild.required<ElementRef<HTMLIFrameElement>>('datawrapperIframe');

  public readonly value = input.required<DatawrapperItemValue>();
  public readonly iframeHeight = signal(370);

  public readonly iframeSrc = computed<SafeResourceUrl | null>(() => {
    const link = this.value().link;
    return link ? this.sanitizer.bypassSecurityTrustResourceUrl(link) : null;
  });

  public onMessage(event: MessageEvent): void {
    const iframeWindow = this.iframe().nativeElement.contentWindow;

    if (!iframeWindow || event.source !== iframeWindow) {
      return;
    }

    const heights = event.data?.['datawrapper-height'];

    if (!heights || typeof heights !== 'object') {
      return;
    }

    const validHeights = Object.values(heights).filter((height): height is number => isNumber(height));

    if (!validHeights.length) {
      return;
    }

    const validHeight = Math.max(...validHeights);

    if (Number.isFinite(validHeight) && validHeight > 0 && validHeight !== this.iframeHeight()) {
      this.iframeHeight.set(validHeight);
    }
  }
}
